'use client'

import { CopyButton, HStack, Link } from '@navikt/ds-react'
import ResultatKortliste from './ResultatKortliste'
import { ArbeidsstedResponse, ArbeidstakerResponse } from '@/api/aaregDistApi'
import { store } from '@/app/_components/store/store'
import { IngenTreff } from '@/components/arbeidsforholdsliste/IngenTreff'
import SortedTable from '@/components/sorted-table'
import { formatDate } from '@/utils/formatting/dato'
import { formatFnrOrgnr } from '@/utils/formatting/tall'
import { lagKopiAriaLabel, settSammenNavn } from '@/utils/formatting/tekst'
import { useErKortliste } from '@/utils/mediaQueries'

const hentNavn = (
    soeker: 'arbeidstaker' | 'arbeidssted',
    data: ArbeidstakerResponse['arbeidsforhold'][0] | ArbeidsstedResponse['arbeidsforhold'][0],
) => {
    switch (soeker) {
        case 'arbeidssted':
            switch (data.arbeidssted.type) {
                case 'organisasjon':
                    return data.arbeidssted.organisasjonsnavn ?? '(Navn mangler)'
                case 'person':
                    return settSammenNavn(data.arbeidssted)
                default:
                    return 'Navn mangler'
            }
        case 'arbeidstaker':
            return settSammenNavn(data.arbeidstaker)
        default:
            return 'Navn mangler'
    }
}
export default function Resultatliste(props: {
    data?: ArbeidsstedResponse | ArbeidstakerResponse
    onNavigateToArbeidsforhold?: (uuid: string) => void
    beforeNavigateToArbeidsforhold?: () => void
}) {
    const { tekst } = store.useReducerState()
    const erKortliste = useErKortliste()
    if (!props.data || !props.data.arbeidsforhold?.length) {
        return <IngenTreff />
    }

    const soeker = 'arbeidstaker' in props.data ? 'arbeidssted' : 'arbeidstaker'
    const identLabel = 'arbeidstaker' in props.data.arbeidsforhold[0] ? tekst.term.foedselsnummer : tekst.term.organisasjonsnummer

    if (erKortliste) {
        return (
            <ResultatKortliste
                arbeidsforhold={props.data.arbeidsforhold}
                soeker={soeker}
                identLabel={identLabel}
                hentNavn={(data) => hentNavn(soeker, data)}
                onNavigateToArbeidsforhold={props.onNavigateToArbeidsforhold}
                beforeNavigateToArbeidsforhold={props.beforeNavigateToArbeidsforhold}
            />
        )
    }

    return (
        <>
            <div>
                <SortedTable
                    fields={[
                        {
                            label: tekst.term.navn,
                            labelStyle: { whiteSpace: 'nowrap' },

                            getValue: (data) => (
                                <Link
                                    as="button"
                                    type="button"
                                    style={{ color: 'var(--ax-text-accent-subtle)' }}
                                    aria-label={'Åpne arbeidsforhold for ' + hentNavn(soeker, data)}
                                    onClick={() => {
                                        props.beforeNavigateToArbeidsforhold?.()
                                        window.sessionStorage.setItem('arbeidsforholdUUID', JSON.stringify(data.uuid))
                                        props.onNavigateToArbeidsforhold?.(data.uuid)
                                    }}
                                >
                                    <span
                                        className="truncate"
                                        style={{
                                            whiteSpace: 'normal',
                                            lineHeight: 'normal',
                                            cursor: 'pointer',
                                        }}
                                    >
                                        {hentNavn(soeker, data)}
                                    </span>
                                </Link>
                            ),
                            sortValue: (data) => hentNavn(soeker, data),
                            maxLines: 1,
                            width: '30%',
                        },
                        {
                            label: identLabel,
                            labelStyle: { whiteSpace: 'nowrap' },
                            getValue: (data) => (
                                <HStack gap="space-4" align={'center'} wrap={false}>
                                    <span style={{ whiteSpace: 'nowrap' }}>{formatFnrOrgnr(data[soeker].offentligIdent)}</span>
                                    <CopyButton
                                        data-color="accent"
                                        size="xsmall"
                                        copyText={data[soeker].offentligIdent}
                                        aria-label={lagKopiAriaLabel(data[soeker].offentligIdent, tekst, hentNavn(soeker, data))}
                                    />
                                </HStack>
                            ),
                            sortValue: (data) => data[soeker].offentligIdent,
                            width: '1%',
                        },
                        {
                            label: tekst.term.startdato,
                            labelStyle: { whiteSpace: 'nowrap' },
                            getValue: (data) => formatDate(data.ansettelsesperiodeStart),
                            sortValue: (data) => data.ansettelsesperiodeStart,
                            width: '1%',
                        },
                        {
                            label: tekst.term.sluttdato,
                            labelStyle: { whiteSpace: 'nowrap' },
                            getValue: (data) => formatDate(data.ansettelsesperiodeSlutt),
                            sortValue: (data) => data.ansettelsesperiodeSlutt,
                            width: '1%',
                        },
                        {
                            label: tekst.term.stillingsprosent,
                            labelStyle: { whiteSpace: 'nowrap' },
                            getValue: (data) => Number(data.gjeldendeAnsettelsesdetaljer.avtaltStillingsprosent).toFixed(2) + ' %',
                            sortValue: (data) => data.gjeldendeAnsettelsesdetaljer.avtaltStillingsprosent,
                            align: 'right',
                            width: '1%',
                        },
                        {
                            label: tekst.term.yrke,
                            labelStyle: { whiteSpace: 'nowrap' },
                            maxLines: 1,
                            getValue: (data) => data.gjeldendeAnsettelsesdetaljer?.yrke?.beskrivelse,
                            sortValue: (data) => data.gjeldendeAnsettelsesdetaljer?.yrke?.beskrivelse,
                            width: '30%',
                        },
                    ]}
                    initialData={props.data.arbeidsforhold}
                    hideRowsPerPage
                />
            </div>
        </>
    )
}
